import React, { Fragment, useCallback, useState } from "react";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import ReactModal from "react-modal";
import moment from "moment-timezone";
import Select from "react-select";

import currencyFormatter from "../../helpers/currencyFormatter";
import { LoadingButton } from "../shared/loading-button";

const targetingPaymentsOptions = [
  { label: "None", value: "" },
  { label: "Clients", value: "clients" },
  { label: "AMEX", value: "amex" },
];

const formatAccountID = (accountID) => {
  if (!accountID) {
    return "-";
  }

  return accountID.replace(
    /([a-zA-Z0-9]+)([a-zA-Z0-9]{6})$/i,
    (_, _middle, last6) => `*****${last6}`,
  );
};

const PlaidLinkedAccountNestedRow = ({ data, disabled, handleModifyAccount }) => {
  const handleChangeTargetingPayments = useCallback((option, { name }) => {
    const [, , id] = name.match(/^([a-zA-Z_]\w*)\[(\w+)\]$/) || [];

    handleModifyAccount(id, { targetingPayments: option?.value || null });
  }, [handleModifyAccount]);

  const handleDisable = useCallback((event) => {
    const { name, checked } = event.target;
    const [, , id] = name.match(/^([a-zA-Z_]\w*)\[(\w+)\]$/) || [];

    handleModifyAccount(id, { disabled: !checked });
  }, [handleModifyAccount]);

  return (
    <Table aria-label="collapsible table">
      <TableHead className="fm-l2-child-table-head dynamic-accordion-table-header">
        <TableRow className="fm-l2-child-table-head-row">
          <TableCell>
            Account ID
          </TableCell>
          <TableCell>
            Name
          </TableCell>
          <TableCell>
            Available Balance
          </TableCell>
          <TableCell>
            Targeting Payments
          </TableCell>
          <TableCell>
            Enabled
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody className="fm-l2-child-table-body">
        {!data.length && (
          <TableRow className="fm-l2-child-table-head-row">
            <TableCell colSpan={5}>No accounts available</TableCell>
          </TableRow>
        )}

        {data.map((account) => (
          <TableRow
            key={account._id}
            sx={{
              backgroundColor: disabled || account.disabled ? "#ffebee !important" : undefined,
            }}
            className="fm-l2-child-table-head-row"
          >
            <TableCell>
              {formatAccountID(account.accountId)}
            </TableCell>
            <TableCell>
              {account.name} • {account.mask}
            </TableCell>
            <TableCell>
              {currencyFormatter.format(account.balances?.[0]?.available)}
            </TableCell>
            <TableCell>
              <Select
                options={targetingPaymentsOptions}
                placeholder="Select"
                name={`targetingPayments[${account._id}]`}
                value={targetingPaymentsOptions.find((option) => option.value === account.targetingPayments) || targetingPaymentsOptions[0]}
                onChange={handleChangeTargetingPayments}
                menuPosition="fixed"
                menuPortalTarget={document.body}
                closeMenuOnSelect
                isDisabled={disabled}
              />
            </TableCell>
            <TableCell>
              <Checkbox
                checked={!disabled && !account.disabled}
                onChange={handleDisable}
                name={`disabled[${account._id}]`}
                disabled={disabled}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const PlaidLinkedAccountRow = ({ data, handleUnlinkAccount, handleModifyAccount }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = useCallback((event) => {
    event.stopPropagation();

    setIsOpen(open => !open);
  }, []);

  const handleUnlink = useCallback((event) => {
    event.stopPropagation();

    handleUnlinkAccount(data._id);
  }, [data, handleUnlinkAccount]);

  if (!data) {
    return null;
  }

  return (
    <Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: data.disabled ? "#ffebee !important" : undefined,
        }}
        onClick={toggleOpen}
        className="fm-l2-parent-table-row"
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={toggleOpen}
            className="p-0"
          >
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {moment(data.createdAt).format("M/D/YY")}
        </TableCell>
        <TableCell>{data.name}</TableCell>
        <TableCell>{data.accounts?.length || 0}</TableCell>
        <TableCell className="text-align-right">
          {data.disabled ? "Unlinked" : <button
            className="base-button icon-button icon-button-small btn-danger"
            onClick={handleUnlink}
            title="Unlink"
          >
            <span className="material-icons">link_off</span>
          </button>}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <PlaidLinkedAccountNestedRow
                data={data.accounts || []}
                disabled={data.disabled}
                handleModifyAccount={handleModifyAccount}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

const PlaidLinkedAccountsTable = ({ data, handleUnlinkAccount, handleModifyAccount }) => {
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [selectedAccessTokenId, setSelectedAccessTokenId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const onUnlinkAccount = useCallback((accessTokenId) => {
    setIsUnlinkModalOpen(true);
    setSelectedAccessTokenId(accessTokenId);
  }, []);

  const handleUnlinkAccountConfirm = useCallback(async () => {
    setIsSaving(true);
    await handleUnlinkAccount(selectedAccessTokenId);
    setIsSaving(false);
    setIsUnlinkModalOpen(false);
    setSelectedAccessTokenId(null);
  }, [selectedAccessTokenId, handleUnlinkAccount]);

  const handleUnlinkAccountCancel = useCallback(() => {
    setIsUnlinkModalOpen(false);
    setSelectedAccessTokenId(null);
  }, []);

  return (
    <Fragment>
      <TableContainer
        component={Paper}
        className="accordion-table-with-details shadow-none"
      >
        <Table aria-label="collapsible table">
          <TableHead className="fm-l2-parent-table-head dynamic-accordion-table-header">
            <TableRow className="fm-l2-parent-table-head-row">
              <TableCell />
              <TableCell>
                Linked Date
              </TableCell>
              <TableCell>
                Bank Name
              </TableCell>
              <TableCell>
                Number of Accounts
              </TableCell>
              <TableCell className="text-align-right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody className="fm-l2-parent-table-body">
            {!data.length && (
              <TableRow
                className="fm-l2-parent-table-row"
              >
                <TableCell colSpan={6}>No accounts linked yet</TableCell>
              </TableRow>
            )}

            {data.length > 0 && data.map((account, index) => (
              <PlaidLinkedAccountRow
                key={`plaid-linked-accounts-table-row-${index}`}
                data={account}
                handleUnlinkAccount={onUnlinkAccount}
                handleModifyAccount={handleModifyAccount}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReactModal
        isOpen={isUnlinkModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleUnlinkAccountCancel}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "calc(min(500px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>
            Unlink Account(s)
          </h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            You are about to unlink bank account(s) from the system This action cannot be undone. Are you sure you want
            to continue?
          </div>
        </div>

        <div className="modal-footer w-100 mt-20 d-flex gap-20">

          <button
            className="base-button evo-blue flex-half"
            type="button"
            onClick={handleUnlinkAccountCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <LoadingButton
            isLoading={isSaving}
            className="base-button btn-danger flex-half"
            type="button"
            onClick={handleUnlinkAccountConfirm}
          >
            Unlink Account(s)
          </LoadingButton>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default PlaidLinkedAccountsTable;
